import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Box, Stack, Typography, Chip,
    Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, Alert,
    CircularProgress, Tooltip, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import { useVrpIterative } from '../../../../hooks/taxi/useVrpIterative';
import VrpRouteMap from './VrpRouteMap';

const ConfirmDialog = ({ open, onClose, onConfirm, loading }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Confirmar optimización
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2">
                Esta operación puede tomar varios segundos y consumir recursos del servidor.
                <br /><br />
                ¿Desea continuar con la optimización de rutas?
            </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} disabled={loading}>Cancelar</Button>
            <Button
                variant="contained"
                color="primary"
                onClick={onConfirm}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            >
                {loading ? 'Calculando...' : 'Sí, optimizar'}
            </Button>
        </DialogActions>
    </Dialog>
);

const VrpIterativeDialog = ({
    open,
    onClose,
    taxiOSId,
    origin,
    paradas = [],
    onPersistSuccess,
    onPersistError,
}) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const {
        status,
        currentCapacity,
        routes,
        acceptedCount,
        totalPassengers,
        selectedIndices,
        error,
        isPersisting,
        persistResult,
        start,
        select,
        commit,
        cancel,
        reset,
        setSelectedIndices,
    } = useVrpIterative(taxiOSId);

    useEffect(() => {
        if (open) {
            reset();
            setShowConfirm(false);
        }
    }, [open, reset]);

    const handleToggleRoute = (index) => {
        setSelectedIndices(
            selectedIndices.includes(index)
                ? selectedIndices.filter(i => i !== index)
                : [...selectedIndices, index]
        );
    };

    const handleSelectAll = () => {
        if (selectedIndices.length === routes.length) {
            setSelectedIndices([]);
        } else {
            setSelectedIndices(routes.map((_, i) => i));
        }
    };

    const handleNext = async () => {
        if (status === 'idle') {
            setShowConfirm(true);
        } else if (status === 'selecting') {
            await select(selectedIndices);
        }
    };

    const handleCommit = async () => {
        try {
            const result = await commit();
            onPersistSuccess?.(result);
            onClose();
        } catch (err) {
            onPersistError?.(err);
        }
    };

    const handleClose = async () => {
        if (status === 'loading' || isPersisting) return;
        if (status !== 'persisted') {
            try {
                await cancel();
            } catch (e) {
                console.warn('Error al cancelar proceso iterativo:', e);
            }
        }
        onClose();
    };

    //const progress = totalPassengers > 0 ? Math.round((acceptedCount / totalPassengers) * 100) : 0;
    const progress = totalPassengers > 0 ? Math.round((acceptedCount / totalPassengers) * 100) : (status === 'finished' || status === 'persisted' ? 100 : 0);

    let primaryAction = null;
    let primaryDisabled = false;
    let primaryLabel = '';
    let primaryIcon = null;

    if (status === 'idle') {
        primaryLabel = 'Iniciar optimización';
        primaryIcon = <AutoGraphIcon />;
        primaryAction = () => setShowConfirm(true);
    } else if (status === 'selecting') {
        primaryLabel = 'Siguiente grupo';
        primaryIcon = <ArrowForwardIcon />;
        primaryDisabled = selectedIndices.length === 0;
        primaryAction = handleNext;
    } else if (status === 'finished' && !isPersisting) {
        primaryLabel = 'Persistir rutas';
        primaryIcon = <SaveIcon />;
        primaryAction = handleCommit;
    } else if (status === 'persisted') {
        primaryLabel = 'Finalizado';
        primaryIcon = <CheckCircleIcon />;
        primaryDisabled = true;
    } else if (status === 'loading' || isPersisting) {
        primaryLabel = status === 'loading' ? 'Optimizando...' : 'Persistiendo...';
        primaryIcon = <CircularProgress size={18} color="inherit" />;
        primaryDisabled = true;
    }

    const renderContent = () => {
        if (status === 'idle' && !error) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 3 }}>
                    <AutoGraphIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                    <Typography variant="h6" fontWeight={500}>Optimización iterativa de rutas</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                        Se optimizarán los pasajeros por grupos de capacidad (de mayor a menor).
                        Usted podrá seleccionar qué rutas aceptar en cada grupo.
                    </Typography>
                    <Button variant="contained" color="primary" size="large" startIcon={<AutoGraphIcon />} onClick={() => setShowConfirm(true)} sx={{ mt: 2, px: 4, py: 1.5 }}>
                        Iniciar optimización
                    </Button>
                </Box>
            );
        }

        if (status === 'loading') {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" fontWeight={500}>Calculando rutas...</Typography>
                    <Typography variant="body2" color="text.secondary">Por favor espere, esto puede tomar unos segundos.</Typography>
                    <LinearProgress sx={{ width: '60%', mt: 2 }} />
                </Box>
            );
        }

        if (status === 'error') {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
                    <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { reset(); setShowConfirm(true); }}>
                        Reintentar
                    </Button>
                </Box>
            );
        }

        if (status === 'selecting' || status === 'finished' || status === 'persisted') {
            const showRoutes = routes.length > 0;
            const isSelecting = status === 'selecting';

            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={500}>
                                Pasajeros asignados: {acceptedCount} / {totalPassengers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Capacidad actual: {currentCapacity || '-'}
                            </Typography>
                            <Chip label={`${Math.round((acceptedCount / (totalPassengers || 1)) * 100)}%`} color="primary" size="small" />
                        </Stack>
                        <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
                    </Box>

                    {status === 'selecting' && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Seleccione las rutas que desea aceptar para esta capacidad. Las no seleccionadas pasarán al siguiente grupo.
                        </Typography>
                    )}

                    {status === 'finished' && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Todos los pasajeros han sido asignados. Presione "Persistir rutas" para guardar los manifiestos.
                        </Alert>
                    )}

                    {status === 'persisted' && persistResult && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Se generaron {persistResult.manifestsCreated} manifiestos para {persistResult.totalPassengers} pasajeros.
                        </Alert>
                    )}

                    {showRoutes && (
                        <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
                            <Box sx={{ flex: '0 0 75%', height: '100%', minHeight: 400 }}>
                                <VrpRouteMap
                                    routes={routes}
                                    origin={origin}
                                    destinations={paradas}
                                    selectedRouteIndices={selectedIndices}
                                    onToggleRoute={handleToggleRoute}
                                />
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto', minWidth: 200 }}>
                                <Paper variant="outlined" sx={{ height: '100%', overflow: 'auto' }}>
                                    <TableContainer component={Paper} elevation={0} sx={{ height: '100%' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {isSelecting && (
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedIndices.length === routes.length && routes.length > 0}
                                                                indeterminate={selectedIndices.length > 0 && selectedIndices.length < routes.length}
                                                                onChange={handleSelectAll}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell>#</TableCell>
                                                    <TableCell>Paradas</TableCell>
                                                    <TableCell align="right">Pax</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {routes.map((route, index) => {
                                                    const isSelected = selectedIndices.includes(index);
                                                    return (
                                                        <TableRow
                                                            key={index}
                                                            hover
                                                            selected={isSelecting && isSelected}
                                                            onClick={() => isSelecting && handleToggleRoute(index)}
                                                            sx={{ cursor: isSelecting ? 'pointer' : 'default' }}
                                                        >
                                                            {isSelecting && (
                                                                <TableCell padding="checkbox">
                                                                    <Checkbox checked={isSelected} onChange={() => handleToggleRoute(index)} />
                                                                </TableCell>
                                                            )}
                                                            <TableCell>{index + 1}</TableCell>
                                                            <TableCell>
                                                                <Tooltip title={route.stops?.map(s => s.locnom).join(' → ')}>
                                                                    <Typography variant="caption" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                                                                        {route.stops?.map(s => s.locnom).join(' → ')}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell align="right">{route.total_passengers}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        }
        return null;
    };

    return (
        <>
            <ConfirmDialog open={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={start} loading={status === 'loading'} />
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="xl"
                disableEscapeKeyDown={status === 'loading' || isPersisting}
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoGraphIcon color="primary" />
                        <Typography variant="h6" fontWeight={800}>Optimización iterativa</Typography>
                        {totalPassengers > 0 && <Chip label={`${acceptedCount}/${totalPassengers} asignados`} size="small" variant="outlined" />}
                        {currentCapacity && <Chip label={`Cap. ${currentCapacity}`} size="small" color="primary" />}
                    </Stack>
                    <IconButton onClick={handleClose} disabled={status === 'loading' || isPersisting}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 2 }}>
                    {renderContent()}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={handleClose} disabled={status === 'loading' || isPersisting}>Cancelar</Button>
                    {primaryAction && (
                        <Button variant="contained" color="primary" onClick={primaryAction} disabled={primaryDisabled} startIcon={primaryIcon} sx={{ minWidth: 150 }}>
                            {primaryLabel}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VrpIterativeDialog;